# Where a signed-in account lands, decided from the account as the server
# holds it (card 6a9cc47fdbd420ed5794d248).
#
# app-route.coffee sent EVERY zero-student account to #/application, the
# enrollment checkout, before it asked whether the account was already paying.
# A parent whose subscription is live (status Subscribed, subscription_end in
# the future — the same test Login.role uses server-side to mint the
# Login.subscribed role) and who has removed every student was walked through
# the whole form, minted a SetupIntent, entered a card, and had the final
# Account.subscribe refused with a raw 401: that role holds no subscribe grant,
# so the friendly "you already have a subscription" 409 behind it can never
# reach the one person it was worded for.
#
# OWNER DECISION 2026-09-06: a routing fix, NOT an ACL grant. A zero-student
# account that is currently Subscribed goes to the Students screen — the
# Students tab of #/account, where a student is added and saved — and never
# into the checkout. Account.subscribe stays closed to Login.subscribed.
#
# CommonJS on purpose, like StudentApplication/enrollRedirect.coffee: webpack
# imports it either way, and the suite requires it and drives it directly.
moment = require 'moment'
{studentRole} = require '../../../common/studentRole.coffee'

# The one definition of "currently subscribed" the client routes on. The
# server's is Login.role (common/models/Login.coffee); keep them the same test
# or the client will route a parent into a form the ACL then refuses.
isActiveSubscription = (account, now = moment()) ->
  return false unless account?.status == 'Subscribed' and account.subscription_end?
  # subscription_end is required explicitly: moment(undefined) is "now", which
  # would read a Subscribed row with no end date as active until the clock
  # ticked. The server's role reads the same row as unsubscribed.
  moment(account.subscription_end).isAfter(now)

# IS THIS STUDENT SESSION SERVED THE SUBSCRIBED MANIFEST? — card
# 6ac3ec94b9e010761617d2cf. Not isActiveSubscription: a student session's
# principal is minted by Student.role, which reads a teacher, school or
# district account by its end date alone, so a teacher on a live Trial plays
# while a lapsed one is locked out. The rule is common/studentRole.coffee, the
# one the server runs, rather than a copy of it.
studentSessionSubscribed = (account, now = moment()) ->
  studentRole(account, now) is 'subscribed'

# WHAT THE ENDED-SUBSCRIPTION SCREEN SAYS to a child on a teacher's account
# whose free trial has run out (owner 2026-10-05, card 6ac3ec94b9e010761617d2cf:
# "The child should go to a screen that says the classroom free trial is
# over."). The family wording — ask a parent to renew — is wrong for a
# classroom. Read off the account the session already holds; the screen makes
# no API call.
classroomTrialEnded = (account) ->
  account?.type is 'teacher' and account.status is 'Trial'

# A paying account with nobody to teach: nothing to buy, someone to add.
subscribedWithoutStudents = (account, students, sharedStudents, now = moment()) ->
  return false unless isActiveSubscription account, now
  (students?.length ? 0) == 0 and (sharedStudents?.length ? 0) == 0

# The enrollment form's Step 2 -> Step 3 gate. The decision must be made on
# the account as the SERVER holds it now: app.account is the tree from the
# last refresh, which can predate a subscription made since (another tab, the
# app store, an agent). So re-read, then decide — and if the re-read fails,
# decide on the last known state rather than strand the parent on a spinner;
# the server's ACL is the backstop either way.
activeSubscriptionAfterRefresh = (app, now = moment()) ->
  try
    await app.refresh()
  catch err
    console.error "Could not re-read the account before Step 3; deciding on the last known state"
    console.dir err
  isActiveSubscription app.account, now

# THE SCHOLARSHIP FORM IS FOR THE FAMILY WHO CANNOT PAY — card
# 6ab167dca4d698cda2b1faa2.
#
# A customer wrote in: the scholarship link
# (https://v2.elephantlearning.com/#/update_scholarship) shows the sign-in page
# instead of the form. What happens after they sign in is the rest of it — the
# target they asked for was dropped on the way.
#
# BOTH ROUTERS DECIDED IT FROM THE SUBSCRIPTION, AND THE SCHOLARSHIP IS THE ONE
# SCREEN THAT CANNOT BE. Every arm that discarded it did so for a family who
# has no live subscription:
#
#   app-route.coffee initialize  a boot straight onto #/update_scholarship went
#     through the subscription shape first. A zero-student account went to the
#     Students screen; a Trial account, or one with no students at all, to
#     #/application. `update_scholarship` was honoured only in the final `else`.
#   login.coffee finish  after signing in, `original_target` was honoured for an
#     ACTIVE subscription, and for a lapsed one ONLY while `status` was still
#     exactly 'Subscribed'. A cancelled or expired account — the family the
#     form exists for — was sent to the enrollment checkout instead.
#
# So the parents who could reach the scholarship form were the ones least
# likely to need it, and the ones asking for help were walked to a payment
# form. It is not a subscription question at all; it is the question below.
#
# ASKED OF THE MANIFEST, like every other "can this session render this screen"
# in these two files (cards 6aaea3e58887cd49ef88bfc3, 6aa6cd1fcbca3e2eac7b42ef).
# The screen calls Account.plans and Account.update_scholarship, and
# Account.json grants both to Login.teacher, Login.subscribed and
# Login.unsubscribed and to nobody else — so an org or district admin, and a
# student-locked session, are refused here rather than sent to a screen that
# would throw on mount. Fail CLOSED when the client API is not there to ask.
scholarshipIsServed = (app) ->
  api = app?.loopbackAPI
  typeof api?.Account?.update_scholarship == 'function' and typeof api?.Account?.plans == 'function'

# The one route name, so neither caller spells it.
SCHOLARSHIP_STATE = 'update_scholarship'

# The deletion page (card 6abdc5fa7009a562af26012d). Like the scholarship form,
# it is ASKED FOR, never routed to by subscription shape: deletion is never
# withheld, and the iOS app opens it in Safari where the parent signs in first.
DELETE_ACCOUNT_STATE = 'delete_account'

# Who may ask for it: a family. Teacher, school and district accounts are
# deleted by staff (owner 2026-10-01, card 6abe8f267009a562af26064f), so their
# portals carry no Delete Account and the page is not honoured for them as a
# target; Account.request_deletion refuses them too
# (common/accountDeletion.coffee selfDeletable).
selfDeletable = (account) ->
  account?.type not in ['teacher', 'organization', 'school', 'district']

# The Students tab of the account screen. ManageAccount/account.coffee builds
# its tabs in order — Account Information, then Students (or Manage Classroom
# for a teacher) — for every account class that reaches this router, so the
# tab index is 1. Organization/app-route.coffee sends its own zero-student
# case to the same place.
STUDENTS_SCREEN_HASH = '#/account?tab=1'

module.exports = {
  isActiveSubscription
  studentSessionSubscribed
  classroomTrialEnded
  subscribedWithoutStudents
  activeSubscriptionAfterRefresh
  scholarshipIsServed
  SCHOLARSHIP_STATE
  DELETE_ACCOUNT_STATE
  selfDeletable
  STUDENTS_SCREEN_HASH
}
